/**
 * Dependency-free Code 128 (subset B) barcode → inline SVG.
 *
 * Hand-rolled (no npm dep) on purpose: adding a barcode library would force a
 * container node_modules reinstall. Code 128B covers ASCII 32–126, which is
 * everything an asset tag / serial uses. Pairs with the QR on the asset label
 * so the label is scannable by both phone cameras (QR) and 1D laser scanners
 * (Code 128) — the industry-standard pairing.
 *
 * Pattern table + checksum are the published Code 128 spec; each pattern is six
 * module-widths (bar,space,bar,space,bar,space). Start-B = 104, Stop = 106.
 */
// 107 patterns (index 0–106). Index 106 (stop) carries its 7-module terminator.
const PATTERNS = [
    '212222', '222122', '222221', '121223', '121322', '131222', '122213', '122312', '132212', '221213',
    '221312', '231212', '112232', '122132', '122231', '113222', '123122', '123221', '223211', '221132',
    '221231', '213212', '223112', '312131', '311222', '321122', '321221', '312212', '322112', '322211',
    '212123', '212321', '232121', '111323', '131123', '131321', '112313', '132113', '132311', '211313',
    '231113', '231311', '112133', '112331', '132131', '113123', '113321', '133121', '313121', '211331',
    '231131', '213113', '213311', '213131', '311123', '311321', '331121', '312113', '312311', '332111',
    '314111', '221411', '431111', '111224', '111422', '121124', '121421', '141122', '141221', '112214',
    '112412', '122114', '122411', '142112', '142211', '241211', '221114', '413111', '241112', '134111',
    '111242', '121142', '121241', '114212', '124112', '124211', '411212', '421112', '421211', '212141',
    '214121', '412121', '111143', '111341', '131141', '114113', '114311', '411113', '411311', '113141',
    '114131', '311141', '411131', '211412', '211214', '211232', '2331112',
];
const START_B = 104;
const STOP = 106;
export function code128bSvg(value, opts = {}) {
    const height = opts.height ?? 40;
    const module = opts.module ?? 2;
    const chars = [...String(value)].filter((c) => {
        const code = c.charCodeAt(0);
        return code >= 32 && code <= 126;
    });
    if (chars.length === 0)
        return '';
    const codes = [START_B];
    chars.forEach((c) => codes.push(c.charCodeAt(0) - 32));
    let sum = START_B;
    chars.forEach((c, i) => {
        sum += (c.charCodeAt(0) - 32) * (i + 1);
    });
    codes.push(sum % 103); // checksum
    codes.push(STOP);
    // Flatten to module widths, then draw bars (even index = bar/black).
    const widths = [];
    codes.forEach((code) => {
        for (const ch of PATTERNS[code] ?? '')
            widths.push(parseInt(ch, 10));
    });
    // Quiet zone (10 modules each side) — required for reliable scanning AND keeps
    // the bars legible on a dark background.
    const quiet = 10 * module;
    const pad = 4;
    let x = quiet;
    const rects = [];
    widths.forEach((w, i) => {
        const px = w * module;
        if (i % 2 === 0)
            rects.push(`<rect x="${x}" y="${pad}" width="${px}" height="${height}" fill="#000"/>`);
        x += px;
    });
    const totalW = x + quiet;
    const totalH = height + pad * 2;
    // White background so black bars are always visible (incl. dark mode) and
    // scannable — Code 128 needs light quiet zones around the bars.
    return (`<svg xmlns="http://www.w3.org/2000/svg" width="${totalW}" height="${totalH}" ` +
        `viewBox="0 0 ${totalW} ${totalH}" shape-rendering="crispEdges">` +
        `<rect x="0" y="0" width="${totalW}" height="${totalH}" fill="#fff"/>${rects.join('')}</svg>`);
}
/** Convenience: the SVG as a data URI for an <img src>. */
export function code128bDataUri(value, opts) {
    const svg = code128bSvg(value, opts);
    return svg ? `data:image/svg+xml;utf8,${encodeURIComponent(svg)}` : '';
}
